import {
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  HStack,
  ModalCloseButton,
  RadioGroup,
  Radio,
  Stack,
  Input,
  Button,
  Divider,
} from "@chakra-ui/react"
import * as Yup from "yup"
import { useEffect, useState } from "react"
import { useFormik, useFormikContext } from "formik"

import { useImportProposalData } from "createProposal/hooks/useImportProposalData"
import type { CreateProposalFormValues } from "createProposal/types/form"

function ImportDetailsModal({
  isOpen,
  onClose,
  setDisplayMarkdownEditor,
}: {
  isOpen: boolean
  onClose: () => void
  setDisplayMarkdownEditor: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const [provider, setProvider] = useState("discourse")

  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()

  const {
    proposalDetails,
    importData: importDetails,
    isLoading: isImportLoading,
  } = useImportProposalData()

  const validationSchema = Yup.object({
    importUrl: Yup.string().url("Invalid URL").required("URL is required"),
  })

  const formik = useFormik({
    initialValues: {
      importUrl: "",
    },
    validationSchema,
    onSubmit: (values) => {
      importDetails(provider, values.importUrl)
      if (provider === "snapshot") {
        setFieldValue("details.snapshotURL", values.importUrl)
      }
    },
  })

  const {
    handleSubmit,
    resetForm,
    handleChange,
    values,
    errors,
    touched,
    isValid,
  } = formik

  const handleProviderChange = (newValue: any) => {
    setProvider(newValue)
  }

  useEffect(() => {
    if (proposalDetails.title && proposalDetails.description) {
      setDisplayMarkdownEditor(false)
      setFieldValue("details.title", proposalDetails.title)
      setFieldValue("details.description", proposalDetails.description)
      // This is to force reload markdown content in the MDXEditor once the props change
      setTimeout(() => {
        setDisplayMarkdownEditor(true)
      }, 100)
    }

    resetForm()
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalDetails])

  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent maxWidth={600} top={{ base: "unset", lg: "120px" }}>
        <ModalHeader
          borderBottomColor="gray.100"
          borderBottomWidth={1}
          p={4}
          textStyle="h5"
        >
          <HStack align="center">
            <Text>Import details</Text>
            <ModalCloseButton />
          </HStack>
        </ModalHeader>
        <ModalBody mb={2} p={0}>
          <Text pt={4} px={4} textStyle="body.regular.lg">
            Provide a Discourse or Snapshot link to quickly import your title
            and description.
          </Text>
          <RadioGroup
            pt={4}
            px={4}
            value={provider}
            onChange={handleProviderChange}
          >
            <Radio mr={4} value="discourse">
              Discourse
            </Radio>
            <Radio value="snapshot">Snapshot</Radio>
          </RadioGroup>
          <Stack p={4}>
            <Text textStyle="body.bold.lg">
              {provider === "discourse" ? "Discourse URL" : "Snapshot URL"}
            </Text>
            <Input
              isInvalid={touched.importUrl && !!errors.importUrl}
              name="importUrl"
              value={values.importUrl}
              onBlur={formik.handleBlur}
              onChange={handleChange}
            />
            {touched.importUrl && errors.importUrl && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {errors.importUrl}
              </Text>
            )}
          </Stack>
        </ModalBody>

        <Divider />
        <HStack justify="end" p={4}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            isDisabled={isImportLoading || !isValid}
            isLoading={isImportLoading}
            variant="primary"
            onClick={() => handleSubmit()}
          >
            Import
          </Button>
        </HStack>
      </ModalContent>
    </Modal>
  )
}

export default ImportDetailsModal
